/**
 * PathBreadcrumb/PinButton/index.tsx
 * desc: pins the current page to the sidebar, or unpins it — the toggle after the last crumb
 * note: hidden until the breadcrumb is hovered or focused and shown while the page is pinned (styles.css). Pinning is
 *       disabled while the list is full (the tooltip says so) or while the label is still empty (the page's name has
 *       not arrived); unpinning needs neither. Both wait while a profile write is in flight
 */

import PinIcon from '../../Icon/PinIcon';
import usePins from '../../../hooks/usePins';
import { classNames } from '../../../utilities';
import { trackEvent } from '../../../utilities/analytics';
import { translate } from '../../../utilities/localization';

interface PinButtonProps {
    label: string;
    url: string;
}

const PinButton = ({ label, url }: PinButtonProps) => {
    //*************************************************
    //*****   Data   **********************************
    //*************************************************
    const { canPin, isPinned, isSaving, pin, unpin } = usePins();
    // Variables
    const pinned = isPinned(url);
    const isFull = !pinned && !canPin;
    let tooltipKey = 'breadcrumb.pin.pin';
    if (pinned) tooltipKey = 'breadcrumb.pin.unpin';
    else if (isFull) tooltipKey = 'breadcrumb.pin.full';


    //*************************************************
    //*****   Public Functions   **********************
    //*************************************************
    const handleClick = () => {
        trackEvent(`Global: Header - Breadcrumb ${pinned ? 'Unpin' : 'Pin'} (Click)`);
        if (pinned) unpin(url);
        else pin({ label, url });
    };


    //*************************************************
    //*****   Render   ********************************
    //*************************************************
    return (
        <PinIcon
            pinned={ pinned }
            className={ classNames('pin-toggle', { pinned }) }
            aria-pressed={ pinned }
            disabled={ isSaving || (!pinned && (isFull || !label)) }
            onClick={ handleClick }
            tooltip={ translate(tooltipKey) }
        />
    );
};

export default PinButton;
